import styled from 'styled-components';
import {
  Button,
  Grid,
  InputAdornment,
  makeStyles,
  TextField
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Employee } from 'types/employee';
import { Meta } from 'types/meta';
import { api } from 'services/api';
import { getTranslation } from 'localization/helpers';
import { useParams } from 'react-router-dom';
import { isNumber } from 'lodash';
import { TipsType } from 'types/tips';
import { initialEmployee, initialMeta } from 'services/initialStates';
import { Framework } from 'client/components/Framework';
import { FullScreen } from 'styles/FullScreen';
import { Loading } from 'common/components/Loading';
import { LoadingError } from 'common/components/LoadingError';
import { Head } from 'common/components/Head';
import { BackgroundImage } from 'styles/BackgroundImage';

const EmployeeName = styled.p`
  padding-top: 20px;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: black;
`;

const ChooseAmountTitle = styled.p`
  padding-top: 30px;
  padding-bottom: 20px;
  font-size: 24px;
  font-weight: 700;
`;

const AnotherAmountTitle = styled.p`
  padding-top: 30px;
  padding-bottom: 20px;
  font-size: 16px;
  font-weight: 500;
`;

const AmountText = styled.p`
  font-size: 26px;
  font-weight: 900;
  margin: 0px;
`;

const CurrencyText = styled.p`
  margin-top: -15px;
  margin-bottom: 0px;
`;

interface ColorProps {
  backgroundColor: string;
}

const CircleButton = styled(Button)<ColorProps>`
  white-space: pre-wrap;
  height: 80px;
  width: 80px;
  border-radius: 50%;
  border: ${props => `${props.backgroundColor} 5px solid`};
  :hover {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

const LeaveTips = styled.div<ColorProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 300;
  background-color: ${props => props.backgroundColor};
  color: white;
  border-radius: 10px;
  padding: 15px;
  margin-top: 30px;
  width: 100%;
  cursor: pointer;
  :hover {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

const FixedAmountButton: FC<{
  amount: number;
  color: string;
  action(): void;
}> = ({ amount, color, action }) => (
  <CircleButton onClick={() => action()} backgroundColor={color}>
    <div>
      <AmountText>{amount}</AmountText>
      <CurrencyText>UAH</CurrencyText>
    </div>
  </CircleButton>
);

export const EmployeePage: FC<{ entityType: TipsType }> = ({ entityType }) => {
  const fixedAmounts = [25, 50, 75];
  const { t, i18n } = useTranslation();
  const { hash } = useParams() as { hash: string };
  const classes = useStyles();
  const [meta, setMeta] = useState<Meta>(initialMeta);
  const [employee, setEmployee] = useState<Employee>(initialEmployee);
  const [amount, setAmount] = useState<number>(fixedAmounts[0]);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<boolean>(false);

  useEffect(() => {
    api()
      .getEmployee(entityType, hash)
      .then(data => {
        if (data.error) setLoadingError(true);
        else {
          setEmployee(data.data.employee);
          setMeta(data.meta);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const name = getTranslation(
    employee.translations,
    'first_name',
    i18n.language,
    meta.default_locale
  );

  const redirectToMerchant = async () => {
    await api()
      .redirectToMerchant(employee.tips, amount)
      .then(data => open(data.redirect, '_self'));
  };

  if (loading) return <Loading />;
  if (loadingError)
    return <LoadingError error={t('failedToRetrieveCompanyData')} />;

  return (
    <FullScreen>
      <BackgroundImage source={meta.background} />
      <Head title={t('EmployeePage')} description={t('EmployeePage')} />
      <Framework
        locales={meta.locales}
        logo={employee.app_avatar}
        color={meta.color}
      >
        <EmployeeName>{name}</EmployeeName>
        <ChooseAmountTitle>{t('chooseAmount')}</ChooseAmountTitle>
        <Grid
          container
          direction="row"
          spacing={3}
          justify="space-around"
          style={{ marginInline: '30px' }}
        >
          {fixedAmounts.map(a => (
            <Grid item xs="auto">
              <FixedAmountButton
                amount={a}
                color={meta.color}
                action={() => setAmount(a)}
              />
            </Grid>
          ))}
        </Grid>
        <AnotherAmountTitle>{t('anotherAmount')}</AnotherAmountTitle>
        <TextField
          value={amount}
          type="number"
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ paddingLeft: '10px' }} position="start">
                UAH
              </InputAdornment>
            ),
            classes: {
              underline: classes.underline,
              input: classes.amountInput
            }
          }}
          variant="standard"
          onChange={e => {
            const num = parseInt(e.currentTarget.value);
            if (isNumber(num)) setAmount(Math.max(1, num));
          }}
        />
        <LeaveTips onClick={redirectToMerchant} backgroundColor={meta.color}>
          {t('leaveTips')}
        </LeaveTips>
      </Framework>
    </FullScreen>
  );
};

const useStyles = makeStyles({
  amountInput: {
    textAlign: 'center',
    fontSize: 26,
    fontWeight: 900
  },
  underline: {
    color: 'black',
    '&::after': {
      border: '1px solid black'
    }
  }
});
